import { useEffect } from 'react';
import { ViewType } from './useNavigation';

interface UseKeyboardNavigationProps {
  activeView: ViewType;
  onViewChange: (view: ViewType) => void;
  canGoBack: boolean;
  onGoBack: () => void;
  validViews: ViewType[];
  onToggleDrawer?: () => void;
  onToggleNavigation?: () => void;
  onFocusSearch?: () => void;
}

export const useKeyboardNavigation = ({
  activeView,
  onViewChange,
  canGoBack,
  onGoBack,
  validViews,
  onToggleDrawer,
  onToggleNavigation,
  onFocusSearch,
}: UseKeyboardNavigationProps) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle keyboard shortcuts when not in an input field
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement
      ) {
        return;
      }

      // Handle keyboard shortcuts
      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case '1':
            event.preventDefault();
            onViewChange('whatsapp');
            break;
          case '2':
            event.preventDefault();
            onViewChange('templates');
            break;
          case '3':
            event.preventDefault();
            onViewChange('send-templates');
            break;
          case '4':
            event.preventDefault();
            onViewChange('phonebook');
            break;
          case 'ArrowLeft':
            if (canGoBack) {
              event.preventDefault();
              onGoBack();
            }
            break;
          case 'b':
          case 'B':
            event.preventDefault();
            onToggleNavigation?.();
            break;
          case 'k':
          case 'K':
            event.preventDefault();
            onFocusSearch?.();
            break;
        }
      }

      // Handle arrow key navigation (without modifier keys)
      if (!event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
        const currentIndex = validViews.indexOf(activeView);

        switch (event.key) {
          case 'ArrowUp':
            event.preventDefault();
            if (currentIndex > 0) {
              onViewChange(validViews[currentIndex - 1]);
            }
            break;
          case 'ArrowDown':
            event.preventDefault();
            if (currentIndex < validViews.length - 1) {
              onViewChange(validViews[currentIndex + 1]);
            }
            break;
          case 'Escape':
            event.preventDefault();
            onToggleDrawer?.();
            break;
        }
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeView, onViewChange, canGoBack, onGoBack, validViews, onToggleDrawer, onToggleNavigation, onFocusSearch]);
};

export default useKeyboardNavigation;